import {
  Avatar,
  FeedCard as SharedFeedCard,
  Icon,
  Tag,
  ModalRoot,
} from '@socialchorus/shared-ui-components';
import { MouseEventHandler, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFeatureFlag from '../../../common/use-feature-flag';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { timeAgoOrDate } from '../../../lib/date-formatter';
import { contentSelectors } from '../../../models/content';
import { Content } from '../../../models/content/types';
import { Feature } from '../../../models/features/features';
import { FeedCardActions } from './feed-card-actions';
import { FeedCardActionLikeButton } from './feed-card-actions/action-like';
import { FeedCardActionComment } from './feed-card-actions/action-comment';
import { programSelectors } from '../../../models/program';
import ContentActionTranslateWrapper from '../../v2/content-actions/content-action-translate-wrapper';
import ContentFrame from '../content-frame';
import { useHistory } from 'react-router';
import styles from './solar-feed-card.module.scss';
import useScreenSize from '../../../common/use-screen-size';
import { MOBILE_SMALL_BREAKPOINT } from '../../../common/breakpoints';
import { ViewLikesModal } from '../../v2/content-actions/view-likes-modal/view-likes-modal';
import { useSolarFeedCardData } from './use-solar-feed-card-data';

function imageLabelWithIcon(iconName: string, text?: string): ReactElement {
  return (
    <SharedFeedCard.ImageLabel position="bottomRight">
      <Icon className="text-caption align-middle" size={16}>
        {iconName}
      </Icon>
      {text ? (
        <span className="text-caption ml-1 align-middle">{text}</span>
      ) : null}
    </SharedFeedCard.ImageLabel>
  );
}

export type FeedCardProps = {
  content: Content;
  analyticsData: IAnalyticsData;
  layout: 'horizontal' | 'vertical' | 'horizontal-minimal' | 'vertical-minimal';
  compact?: boolean;
  titleHeadingLevel?: 2 | 3 | 4;
  fullHeight?: boolean;
  inlineVideo?: boolean;
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;
  onClick?: () => void;
  showPinnedLabel?: boolean;
  /** "detailed" hero (FE-32783): render the body excerpt below the description.
   * Only fills space in a vertical + fullHeight card (the hero). */
  showBodyPreview?: boolean;
};

export function FeedCardWithData({
  content,
  analyticsData,
  layout: layoutProp,
  compact,
  titleHeadingLevel,
  fullHeight,
  onClick,
  lineClamp,
  inlineVideo = false,
  showPinnedLabel = false,
  showBodyPreview = false,
}: FeedCardProps) {
  const history = useHistory();
  const { width } = useScreenSize();

  // Legacy from the old feed card: a horizontal card is too cramped below this
  // width, so it renders vertical instead.
  const isMobile = width < MOBILE_SMALL_BREAKPOINT;
  // The two minimal layouts ARE the mobile treatment — don't coerce them.
  const isMinimalLayout =
    layoutProp === 'horizontal-minimal' || layoutProp === 'vertical-minimal';
  const layout = isMobile && !isMinimalLayout ? 'vertical' : layoutProp;

  //controls minor layout changes associated with the reaction types deployment.
  const expandedReactionTypesDeployed = useFeatureFlag(
    Feature.EXPANDED_REACTION_TYPES_DEPLOY
  );
  //controls the reaction types feature
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );

  const {
    ref,
    href,
    onClick: handleCardClick,
    title,
    description,
    cardImage,
    avatarSrc,
    contentAuthorLink,
    getLinkNavigation,
  } = useSolarFeedCardData(content, analyticsData, { onClick });

  const reactions = usePatronSelector((state) =>
    contentSelectors.getContentReactions(state, { contentId: content.id })
  );
  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId: content.id })
  );
  const viewCount = usePatronSelector((s) =>
    contentSelectors.getContentViewCount(s, { contentId: content.id })
  );
  const viewsEnabled = usePatronSelector((s) =>
    programSelectors.getFeedCardViewsEnabled(s)
  );
  const publishedAt = usePatronSelector((s) =>
    contentSelectors.getContentPublishedAt(s, { contentId: content.id })
  );

  const isVideoType = content.content_type === 'video';

  // Same gotcha as title/description in useSolarFeedCardData: read from the
  // selector, not the prop, so the excerpt picks up the translated value the
  // Translate button writes to the store (the prop is the original feed-query
  // object).
  const bodyExcerpt = usePatronSelector((state) =>
    contentSelectors.getContentBodyExcerpt(state, { contentId: content.id })
  );

  const { t } = useTranslation();

  //controls the video player being overlaid on the thumbnail.
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [showReactions, setShowReactions] = useState<boolean>(false);

  // Reset video state on location change
  useEffect(() => {
    let historyUnlisten;

    if (inlineVideo && showVideo) {
      historyUnlisten = history?.listen(() => {
        setShowVideo(false);
      });
    }
    return historyUnlisten;
  }, [showVideo, history, inlineVideo]);

  // Video/multi-image overlay labels are only relevant to the full card —
  // the minimal mobile compositions don't render them.
  let imageLabel: ReactElement | undefined;
  if (content.content_type !== 'note') {
    // Note: draft video content may have no video uploaded
    if (content.content_type === 'video' && content.video) {
      imageLabel = imageLabelWithIcon(
        'play_circle',
        content.video?.duration != null
          ? String(content.video.duration)
          : undefined
      );
    } else if (
      !!content.background_image_urls &&
      content.background_image_urls?.length > 1
    ) {
      imageLabel = imageLabelWithIcon(
        'image',
        content.background_image_urls.length + ''
      );
    }
  }

  const viewWord = t('content.view', {
    count: viewCount,
  });

  const additionalMetadata = [];
  if (expandedReactionTypesDeployed) {
    if (!expandedReactionTypesEnabled) {
      additionalMetadata.push(
        t('content.likes_with_count', { count: likeCount })
      );
    } else {
      const reactionTotal = reactions.reduce((a, b) => a + b.count, 0);
      additionalMetadata.push(
        t('content.reactions_with_count', {
          count: reactionTotal,
        })
      );
    }
  } else {
    additionalMetadata.push(publishedAt ? timeAgoOrDate(publishedAt) : '-');
  }
  if (viewsEnabled) {
    const viewDisplayText =
      t('compact_number', { value: viewCount }) + ` ${viewWord}`; //this is invalid for translation, the correct order of word and number is different per language. revisit when views is officially made into a feature.

    additionalMetadata.push(viewDisplayText);
  }

  const pinned = showPinnedLabel && content.pinned;

  const buttonText = content.buttons?.[0]?.buttonText; //featured label text
  const labelText = pinned
    ? t('content.pinned')
    : typeof buttonText === 'string' //guard against buttonText being something crazy like an object, this did happen in prod and caused a p1
    ? buttonText
    : undefined;

  const reactionsDisabled = useFeatureFlag(Feature.REACTIONS_DISABLED);

  const top3ReactionTypes =
    reactions
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map((reaction) => reaction.emoji) || [];

  const handlePlayButtonClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowVideo(true);
  };

  // Only allow clicking to open the reactions modal when there is at least
  // one like (legacy) or one reaction (expanded), otherwise keep it static.
  const reactionTotal = reactions.reduce((a, b) => a + b.count, 0);
  const metadataIsClickable =
    expandedReactionTypesDeployed &&
    (expandedReactionTypesEnabled ? reactionTotal > 0 : likeCount > 0);

  const metadataAriaLabel = expandedReactionTypesEnabled
    ? t('view_reactions_action')
    : t('view_likes_action');

  return (
    <>
      <SharedFeedCard.Root
        ref={ref}
        layout={layout}
        href={href}
        fullHeight={fullHeight}
        compact={compact}
        title={title}
        description={description}
        headingLevel={titleHeadingLevel}
        generateMentionHref={(userId) =>
          getLinkNavigation(`/profiles/${userId}`)
        }
        onClick={handleCardClick}
      >
        <SharedFeedCard.Image
          image={cardImage}
          renderOverride={(imageComponent) => {
            return inlineVideo && isVideoType ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                className={styles.VideoPlayContainer}
                onClick={(e) => {
                  //make sure clicking in this region doesn't trigger the card click
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {imageComponent}
                {!showVideo ? (
                  <button
                    className={styles.VideoPlayOverlayButton}
                    onClick={handlePlayButtonClick}
                  >
                    <Icon size={40} className={styles.VideoPlayIcon}>
                      play_circle
                    </Icon>
                  </button>
                ) : (
                  <ContentFrame
                    className={styles.VideoFrame}
                    contentId={content.id}
                    analyticsData={analyticsData}
                    autoPlayVideo={true} //autoplay on as this will only be rendered by a user clicking on the overlay
                  />
                )}
              </div>
            ) : (
              imageComponent
            );
          }}
        >
          {labelText ? (
            <SharedFeedCard.ImageLabel position="topRight">
              {content.pinned ? (
                <Icon className="text-caption align-middle" size={18}>
                  push_pin
                </Icon>
              ) : null}
              {labelText}
            </SharedFeedCard.ImageLabel>
          ) : undefined}
          {imageLabel}
        </SharedFeedCard.Image>
        <SharedFeedCard.Main>
          <SharedFeedCard.Content>
            {expandedReactionTypesDeployed && (
              <SharedFeedCard.ContentHeader
                authorAvatarComponent={
                  <Avatar imgSrc={avatarSrc} shape="circle" size="small" />
                }
                authorName={content.source_name}
                authorHref={contentAuthorLink}
                publishDate={publishedAt ? timeAgoOrDate(publishedAt) : '-'}
              />
            )}
            <SharedFeedCard.Title />
            <SharedFeedCard.Description lineClamp={lineClamp} />
            {showBodyPreview && bodyExcerpt ? (
              <SharedFeedCard.BodyPreview text={bodyExcerpt} />
            ) : null}
            <ContentActionTranslateWrapper
              contentId={content.id}
              analyticsData={analyticsData}
              includeBodyExcerpt={showBodyPreview}
            >
              {({ canTranslate, handleClick, isTranslated }) => {
                return canTranslate ? (
                  <SharedFeedCard.TranslateButton
                    isTranslated={isTranslated}
                    onClick={handleClick}
                  />
                ) : null;
              }}
            </ContentActionTranslateWrapper>
          </SharedFeedCard.Content>
          {
            <SharedFeedCard.Tags
              display={1}
              popoverTitle={t('content.posted_in_topics')}
              popoverWidth="200px"
              tags={content.content_channels.map((channel) => (
                <Tag
                  key={channel.id}
                  label={channel.name}
                  href={getLinkNavigation(`/channels/${channel.id}`)}
                />
              ))}
            />
          }
        </SharedFeedCard.Main>

        <SharedFeedCard.Footer>
          {metadataIsClickable ? (
            //assumption that metadataIsClickable already does the expandedReactionTypesDeployed check
            <SharedFeedCard.Metadata
              additionalMetadata={additionalMetadata}
              displayLikeIcon={!expandedReactionTypesEnabled}
              reactedEmojis={
                expandedReactionTypesEnabled ? top3ReactionTypes : undefined
              }
              onClick={() => setShowReactions(true)}
              ariaLabel={metadataAriaLabel}
            />
          ) : (
            <SharedFeedCard.Metadata
              additionalMetadata={additionalMetadata}
              displayLikeIcon={
                !!expandedReactionTypesDeployed && !expandedReactionTypesEnabled
              }
              reactedEmojis={
                expandedReactionTypesEnabled ? top3ReactionTypes : undefined
              }
              authorAvatarComponent={
                expandedReactionTypesDeployed ? undefined : (
                  <Avatar imgSrc={avatarSrc} shape="circle" size="small" />
                )
              }
              authorName={
                expandedReactionTypesDeployed ? undefined : content.source_name
              }
              authorHref={
                expandedReactionTypesDeployed ? undefined : contentAuthorLink
              }
            />
          )}

          {!reactionsDisabled ? (
            <FeedCardActions content={content} analyticsData={analyticsData} />
          ) : null}
        </SharedFeedCard.Footer>
      </SharedFeedCard.Root>

      <ModalRoot
        open={showReactions}
        onOpenChange={(setOpen) => setShowReactions(setOpen)}
      >
        <ViewLikesModal contentId={content.id} />
      </ModalRoot>
    </>
  );
}

export type MobileListCardProps = {
  content: Content;
  analyticsData: IAnalyticsData;
  titleHeadingLevel?: 2 | 3 | 4;
};

/**
 * Grid module's mobile-layouts row: image + author + title, plus a description
 * the card reveals only once it is wide enough for Design's larger tier. Title
 * and description are deliberately left unclamped — `horizontal-minimal`
 * derives both line counts and that reveal from its own width, and any explicit
 * clamp opts out, collapsing the two tiers into one.
 */
export function MobileListCard({
  content,
  analyticsData,
  titleHeadingLevel,
}: MobileListCardProps) {
  const {
    ref,
    href,
    onClick,
    title,
    description,
    cardImage,
    avatarSrc,
    authorName,
    contentAuthorLink,
  } = useSolarFeedCardData(content, analyticsData);

  return (
    <SharedFeedCard.Root
      ref={ref}
      layout="horizontal-minimal"
      href={href}
      title={title}
      description={description}
      headingLevel={titleHeadingLevel}
      onClick={onClick}
    >
      <SharedFeedCard.Image image={cardImage} />
      <SharedFeedCard.Main>
        <SharedFeedCard.Content>
          <SharedFeedCard.ContentHeader
            authorAvatarComponent={
              <Avatar imgSrc={avatarSrc} shape="circle" size="extra-small" />
            }
            authorName={authorName}
            authorHref={contentAuthorLink}
          />
          <SharedFeedCard.Title />
          <SharedFeedCard.Description />
        </SharedFeedCard.Content>
      </SharedFeedCard.Main>
    </SharedFeedCard.Root>
  );
}

export type MobileScrollCardProps = {
  content: Content;
  analyticsData: IAnalyticsData;
  /** 'trimmed' is image + title + description + centered like/comment —
   * used for a module's secondary scroll cards. 'full' is the full-density
   * card — same information as the legacy card, re-skinned onto
   * `vertical-minimal` — used for Row's whole scroll and Hero's standalone
   * featured card. */
  composition: 'trimmed' | 'full';
  titleHeadingLevel?: 2 | 3 | 4;
};

function MobileTrimmedScrollCard({
  content,
  analyticsData,
  titleHeadingLevel,
}: Omit<MobileScrollCardProps, 'composition'>) {
  const { ref, href, onClick, title, description, cardImage } =
    useSolarFeedCardData(content, analyticsData);
  const reactionsDisabled = useFeatureFlag(Feature.REACTIONS_DISABLED);

  // Mirror the visibility rules the toolbar enforces everywhere else
  // (see feed-card-actions ContentCardToolbarActions): no engagement when
  // reactions are off, like only on published content, comment only when
  // the post is published and commentable.
  const isPublished = content.publication_state === 'published';
  const showLike = !reactionsDisabled && isPublished;
  const showComment =
    !reactionsDisabled && isPublished && content.is_commentable;

  return (
    <SharedFeedCard.Root
      ref={ref}
      layout="vertical-minimal"
      href={href}
      title={title}
      description={description}
      headingLevel={titleHeadingLevel}
      onClick={onClick}
    >
      <SharedFeedCard.Image image={cardImage} />
      <SharedFeedCard.Main>
        <SharedFeedCard.Content>
          <SharedFeedCard.Title />
          {/* Three lines per the card's spec, above Solar's default of two. */}
          <SharedFeedCard.Description lineClamp={3} />
        </SharedFeedCard.Content>
      </SharedFeedCard.Main>
      {showLike || showComment ? (
        <SharedFeedCard.Actions align="center">
          {showLike ? (
            <FeedCardActionLikeButton
              content={content}
              analyticsData={analyticsData}
              isHidden={false}
            />
          ) : null}
          {showComment ? (
            <FeedCardActionComment
              content={content}
              analyticsData={analyticsData}
              isHidden={false}
            />
          ) : null}
        </SharedFeedCard.Actions>
      ) : null}
    </SharedFeedCard.Root>
  );
}

/**
 * Hero/Row modules' mobile-layouts scroll card. The 'full' preset delegates to
 * `FeedCardWithData` — the full-density mobile card carries the exact same
 * composition as the legacy card, just on `vertical-minimal` — so there is
 * nothing to re-implement.
 */
export function MobileScrollCard({
  content,
  analyticsData,
  composition,
  titleHeadingLevel,
}: MobileScrollCardProps) {
  if (composition === 'full') {
    return (
      <FeedCardWithData
        content={content}
        analyticsData={analyticsData}
        layout="vertical-minimal"
        compact
        fullHeight
        titleHeadingLevel={titleHeadingLevel}
      />
    );
  }

  return (
    <MobileTrimmedScrollCard
      content={content}
      analyticsData={analyticsData}
      titleHeadingLevel={titleHeadingLevel}
    />
  );
}
